import {
  Box,
  Typography,
  Button,
  Stack,
  Paper,
  TextField,
  IconButton,
  Alert,
} from '@mui/material'
import { useState, useEffect } from 'react'
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material'
import type { WhitelistEntry } from '@/types'
import { useInvoiceAnalyser } from '@/hooks/useInvoiceAnalyser'
import { useT } from '@/i18n/useT'

interface RecipientWhitelistSectionProps {
  open?: boolean
}

export default function RecipientWhitelistSection({ open }: RecipientWhitelistSectionProps) {
  const [recipients, setRecipients] = useState<WhitelistEntry[]>([])
  const { t } = useT()
  const [loading, setLoading] = useState(false)
  const [showAdd, setShowAdd] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const [newRecipient, setNewRecipient] = useState<WhitelistEntry>({
    id: '',
    names: [''],
    normalizedName: '',
    ustId: ''
  })

  const {
    getRecipientWhitelist,
    addRecipientToWhitelist,
    deleteRecipientFromWhitelist,
  } = useInvoiceAnalyser()

  // Load whitelist when dialog opens
  useEffect(() => {
    if (open) {
      loadWhitelist()
    }
  }, [open])

  const loadWhitelist = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await getRecipientWhitelist()
      setRecipients(data.recipients)
    } catch (err) {
      console.error('Failed to load recipient whitelist:', err)
      setError(t('Could not load the whitelist'))
    } finally {
      setLoading(false)
    }
  }

  const handleAdd = async () => {
    if (!newRecipient.id || !newRecipient.normalizedName || !newRecipient.ustId) {
      setError(t('Please fill in all required fields'))
      return
    }

    const names = newRecipient.names[0]
      ? newRecipient.names[0].split(',').map(s => s.trim()).filter(Boolean)
      : [newRecipient.normalizedName]

    const recipientToAdd: WhitelistEntry = {
      ...newRecipient,
      names
    }

    try {
      await addRecipientToWhitelist(recipientToAdd)
      await loadWhitelist()
      setShowAdd(false)
      setNewRecipient({ id: '', names: [''], normalizedName: '', ustId: '' })
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    } catch (err) {
      console.error('Failed to add recipient:', err)
      setError(err instanceof Error ? err.message : t('Could not add the recipient'))
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await deleteRecipientFromWhitelist(id)
      await loadWhitelist()
    } catch (err) {
      console.error('Failed to delete recipient:', err)
      setError(t('Could not delete the recipient'))
    }
  }

  const generateId = (name: string) => {
    return name.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="subtitle2">{t('Recipient whitelist')}</Typography>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setShowAdd(!showAdd)}
        >
          {t('Add recipient')}
        </Button>
      </Stack>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {t('Recipient added')}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {showAdd && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
            {t('Add a new recipient to the whitelist')}
          </Typography>
          <Stack spacing={1}>
            <TextField
              size="small"
              label={t('ID')}
              value={newRecipient.id}
              onChange={(e) => setNewRecipient({ ...newRecipient, id: e.target.value })}
              placeholder={`${t('e.g.')} my-company`}
              onBlur={() => {
                if (!newRecipient.id && newRecipient.normalizedName) {
                  setNewRecipient({ ...newRecipient, id: generateId(newRecipient.normalizedName) })
                }
              }}
            />
            <TextField
              size="small"
              label={t('Normalised name')}
              value={newRecipient.normalizedName}
              onChange={(e) => setNewRecipient({ ...newRecipient, normalizedName: e.target.value })}
              placeholder={`${t('e.g.')} My Company GmbH`}
            />
            <TextField
              size="small"
              label={t('VAT ID')}
              value={newRecipient.ustId}
              onChange={(e) => setNewRecipient({ ...newRecipient, ustId: e.target.value })}
              placeholder={`${t('e.g.')} DE123456789`}
            />
            <TextField
              size="small"
              label={t('Name variations (comma-separated)')}
              value={newRecipient.names[0] || ''}
              onChange={(e) => setNewRecipient({
                ...newRecipient,
                names: [e.target.value]
              })}
              placeholder={`${t('e.g.')} My Company, Company GmbH`}
              helperText={t('Different ways this recipient might appear on invoices')}
            />
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                size="small"
                onClick={handleAdd}
                disabled={!newRecipient.id || !newRecipient.normalizedName || !newRecipient.ustId}
              >
                {t('Add to whitelist')}
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setShowAdd(false)
                  setNewRecipient({ id: '', names: [''], normalizedName: '', ustId: '' })
                }}
              >
                {t('Cancel')}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      {loading ? (
        <Typography variant="body2" color="textSecondary">
          {t('Loading whitelist …')}
        </Typography>
      ) : recipients.length === 0 ? (
        <Typography variant="body2" color="textSecondary">
          {t('No recipients in the whitelist yet — add the first one above.')}
        </Typography>
      ) : (
        <Stack spacing={1}>
          {recipients.map((recipient) => (
            <Paper key={recipient.id} variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={500}>
                    {recipient.normalizedName}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {t('ID')}: {recipient.id} • {t('VAT ID')}: {recipient.ustId || '—'}
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Typography variant="caption" color="textSecondary" sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      display: 'block'
                    }}>
                      {t('Variations')}: {recipient.names.join(', ')}
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => handleDelete(recipient.id)}
                  color="error"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}

      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
        {t('Recipients in the whitelist')}: {recipients.length}
      </Typography>
    </Box>
  )
}
